import { NextApiService } from '@services/next-api-service';
import { Component, Event, EventEmitter, h, Host, State } from '@stencil/core';
import { pathStore } from '@store/path-store';
import { DiscoverPageDataProps } from './components/types';
interface SectionProps {
  __component: string;
  id: number;
  [key: string]: any;
}

@Component({
  tag: 'discover-page',
  styleUrl: 'discover-page.scss',
  shadow: true
})
export class DiscoverPage {
  @State() discoverResponse: DiscoverPageDataProps;
  @State() showDiscoverModal: boolean = false;
  @Event() closeDiscover!: EventEmitter<void>;
  @State() isLoading: boolean = true;
  @State() error: string | null = null;
  private nextApiService = new NextApiService();

  private touchStartY = 0;
  private touchEndY = 0;

  private componentMap: Record<string, (props: any) => any> = {
    'sections.unbeateable-deals': props => <section-unbeateable-deals {...props} />,
    'sections.card-benefits-icons': props => <card-benefits {...props} />,
    'sections.curated-offers': props => <section-curated-offers {...props} />,
    'sections.explore': props => <explore-sec {...props} />
  };

  private emitCloseEvent = () => {
    this.closeDiscover.emit();
  };

  private handleTouchStart = (e: TouchEvent) => {
    this.touchStartY = e.touches[0].clientY;
  };

  private handleTouchEnd = (e: TouchEvent) => {
    this.touchEndY = e.changedTouches[0].clientY;
    const swipeDistance = this.touchEndY - this.touchStartY;

    if (swipeDistance > 400) {
      this.emitCloseEvent();
    }
  };

  getCmsData = async () => {
    this.isLoading = true;
    this.error = null;
    try {
      const input = encodeURIComponent(JSON.stringify({ '0': { json: { cardType: 'default' } } }));
      const response = await this.nextApiService.get(`/cmsComponentsRouter.fetchDiscoverCmsData?batch=1&input=${input}`);
      this.discoverResponse = response;
    } catch (error) {
      console.log(error);
      this.error = 'Something went wrong while loading this page. Please try again.';
    } finally {
      this.isLoading = false;
    }
  };

  componentWillLoad() {
    this.getCmsData();
  }

  render() {
    const layout = this.discoverResponse?.HomePageLayout;
    const hasValidLayout = this.discoverResponse && Array.isArray(layout) && layout.length > 0;
    const assetPath = pathStore.assetPath;
    return (
      <Host>
        <slot>
          <div class="scroll-container pb-20" onTouchStart={this.handleTouchStart} onTouchEnd={this.handleTouchEnd}>
            {/* Header */}
            <div class="p-4 top-fixed-bg sticky top-0 z-20">
              <div class="flex gap-2 justify-between">
                <div>
                  <p class="text-base font-bold text-white leading-6">Explore More</p>
                  <p class="text-xs sec-text-color">Use cash, reward points or both!</p>
                </div>
                <button onClick={() => this.emitCloseEvent()}>
                  <img class="w-6 h-6" alt="" src={assetPath + 'icons/closeWhite.svg'} />
                </button>
              </div>
            </div>

            {this.isLoading ? (
              <div class="mb-6">
                {Array.isArray(layout) && layout.length > 0 ? (
                  layout.map((section: SectionProps) => {
                    switch (section.__component) {
                      case 'sections.unbeateable-deals':
                        return <unbeateable-deals-skeleton />;
                      case 'sections.card-benefits-icons':
                        return <card-benefits-skeleton />;
                      case 'sections.curated-offers':
                        return <curated-offers-skeleton />;
                      case 'sections.explore':
                        return <card-benefits-skeleton />;
                      default:
                        return null;
                    }
                  })
                ) : (
                  <>
                    <card-benefits-skeleton />
                    <curated-offers-skeleton />
                  </>
                )}
              </div>
            ) : this.error ? (
              <div class="text-center text-sm text-red-500 py-10 px-4">
                <p>{this.error}</p>
                <button onClick={() => this.getCmsData()} class="mt-4 px-4 py-2 bg-red-500 text-white rounded-md text-sm">
                  Retry
                </button>
              </div>
            ) : hasValidLayout ? (
              layout.map((section: SectionProps) => {
                const Component = section?.__component ? this.componentMap[section.__component] : null;
                if (!Component) return null;
                return (
                  <div class="mb-6" key={section.id || section.__component}>
                    {Component({
                      data: section
                    })}
                  </div>
                );
              })
            ) : (
              <div class="text-center text-sm text-gray-400 py-10">No sections available to display.</div>
            )}
          </div>
        </slot>
      </Host>
    );
  }
}
